// ============================================================================
// FeedbackButton — floating bottom-right pill, opens a modal that submits
// to public.submit_feedback(...) RPC. Mounted globally inside NavShell so
// it appears on every authenticated screen.
//
// Hidden when:
//   - user not signed in (no profile)
//   - on /admin/feedback itself (would be confusing — they're already
//     triaging feedback, button would just feel noisy)
//   - on /demo (design preview, no auth)
//   - on any pathname listed in the `hideOnRoutes` prop (startsWith match) —
//     used by routes whose own primary FAB (e.g. /hr-hiring's "+" button)
//     would otherwise collide with this floating control.
//
// DB layer: app_feedback table + submit_feedback RPC (migration:
// create_app_feedback, 2026-05-10). RPC grants authenticated execute.
// ============================================================================

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, Send, X } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import { Button } from '../../shared/components';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';
import { isCategoryWordTitle } from './title-guard';

type FeedbackType = 'bug' | 'feature_request' | 'improvement' | 'question' | 'other';

const TYPE_OPTIONS: Array<{ value: FeedbackType; label: string; hint: string }> = [
  { value: 'bug', label: 'Bug', hint: 'Something is broken or wrong' },
  { value: 'feature_request', label: 'Feature request', hint: 'I want something new' },
  { value: 'improvement', label: 'Improvement', hint: 'Something works but could be better' },
  { value: 'question', label: 'Question', hint: 'How do I…?' },
  { value: 'other', label: 'Other', hint: '' },
];

export function FeedbackButton({ hideOnRoutes = [] }: { hideOnRoutes?: string[] } = {}) {
  const [open, setOpen] = useState(false);
  const { profile } = useAuth();
  const location = useLocation();

  // Hide on demo and on the admin triage view itself.
  if (!profile) return null;
  if (location.pathname.startsWith('/admin/feedback')) return null;
  if (location.pathname === '/demo') return null;
  // Caller-supplied hide list — used for routes whose own primary FAB would
  // collide with this floating control (e.g. /hr-hiring's "+" button).
  if (hideOnRoutes.some((r) => location.pathname.startsWith(r))) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // Home carries the Ask AI bar just above the phone tab bar; on a phone
        // the button sits above that bar instead of covering its Ask button.
        className={`fixed ${location.pathname === '/' ? 'bottom-[calc(136px+env(safe-area-inset-bottom))] md:bottom-20' : 'bottom-20 md:bottom-4'} right-4 z-40 inline-flex items-center gap-2 rounded-full bg-tcs-accent-ink px-4 py-3 text-sm font-medium text-tcs-accent-on shadow-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-tcs-accent focus:ring-offset-2`}
        aria-label="Send feedback"
      >
        <MessageCircle className="h-4 w-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>
      {open && <FeedbackModal onClose={() => setOpen(false)} />}
    </>
  );
}

function FeedbackModal({ onClose }: { onClose: () => void }) {
  const [type, setType] = useState<FeedbackType>('improvement');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const location = useLocation();

  async function handleSubmit() {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    // W-06. See ./title-guard for why this is a nudge and not a leak being
    // plugged. Not a silent rewrite: the person still chooses the words.
    if (isCategoryWordTitle(title, TYPE_OPTIONS.map((o) => o.value))) {
      setError(
        `"${title.trim()}" is the category, not a title — you've already picked that above. ` +
          'Give it a one-line summary someone could recognise in a list.',
      );
      return;
    }
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('submit_feedback', {
        p_type: type,
        p_source: 'button',
        p_title: title.trim(),
        p_description: description.trim(),
        p_module: null,
        p_url_path: location.pathname,
        p_chat_excerpt: null,
        p_user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
      if (rpcErr) throw rpcErr;
      setSubmitted((data as { feedback_id?: string })?.feedback_id ?? 'logged');
    } catch (e) {
      console.error('[feedback] submit failed', e);
      setError(
        errorMessage(e, 'Could not submit. Please try again.'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4"
      {...overlayClickClose(onClose)}
      role="dialog"
      aria-modal="true"
      aria-label="Send feedback"
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-tcs-overlay p-5 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-tcs-text">
              Send feedback
            </h2>
            <p className="mt-0.5 text-xs text-tcs-text-muted">
              Goes straight to the COO. Bugs, ideas, anything that bugs you.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-tcs-text-secondary hover:bg-tcs-border hover:text-tcs-text"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {submitted ? (
          <div className="mt-4 rounded-lg border border-st-ok-border bg-st-ok-bg p-4 text-sm text-st-ok-fg-strong">
            <p className="font-medium">Thanks — logged.</p>
            <p className="mt-1 text-xs">
              Reference: <code className="font-mono">{submitted}</code>
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {/* Type */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                Type
              </label>
              <div className="mt-1.5 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setType(opt.value)}
                    className={`rounded-md border px-2 py-1.5 text-xs font-medium transition ${
                      type === opt.value
                        ? 'border-tcs-accent bg-tcs-accent/10 text-tcs-accent-ink'
                        : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:border-tcs-text-secondary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label
                htmlFor="fb-title"
                className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted"
              >
                Title
              </label>
              <input
                id="fb-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="One-line summary"
                maxLength={200}
                className="mt-1.5 w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
              />
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="fb-desc"
                className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted"
              >
                Details
              </label>
              <textarea
                id="fb-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What happened? What did you expect? Any steps to reproduce?"
                rows={5}
                className="mt-1.5 w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none"
              />
            </div>

            {/* Context preview — readonly, just so the user knows we captured it */}
            <div className="rounded-md bg-tcs-bg-muted px-2 py-1.5 text-[11px] text-tcs-text-muted">
              <span className="font-medium">Captured automatically:</span>{' '}
              <code className="font-mono">{location.pathname}</code>
            </div>

            {error && (
              <p className="rounded-md bg-st-danger-bg px-2 py-1.5 text-xs text-st-danger-fg-strong">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="secondary"
                size="sm"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={submitting}
                icon={<Send className="h-3.5 w-3.5" />}
              >
                {submitting ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
